/**
 * Chart Studio → Configure → Chart type picker catalogue, as pure data.
 * Figma: `z2UTps4F1dKNh1bbD4LtYI` node `6433:562527` ("List #1").
 *
 * Same contract as `dataModelFieldSections.ts`: structure and copy live here,
 * the picker's open/query/selection state lives at the usage site keyed by
 * `id`. Never JSX, never callbacks. Adding, renaming or regrouping a chart
 * type is an edit to this file only — no picker JSX changes.
 *
 * Two readers share it:
 * - the v1 picker groups the rows that carry a `category` (the 27 original
 *   types, in Figma order) and offers those with a `binding`;
 * - the v2 gallery (`ChartTypeGallery`) shows every row, grouped by `family`.
 *   A row's `id` is a gallery entry's `id` in the chart catalog served by the
 *   backend (`GET /api/v2/chart-catalog`), which owns the type and the
 *   options preset; this file owns only the copy and the glyph. A row's
 *   `family` must equal the catalog's (a test keeps the two in step).
 */

/** Picker column groups. Also the future API's `category` value, untranslated. */
export type ChartTypeCategoryId =
  | "comparison"
  | "trend"
  | "distribution"
  | "ranking"
  | "part-to-whole"
  | "summary";

/**
 * The v2 gallery's family tabs. The values are the catalog's `family` values
 * (snake case), untranslated: a type's family comes from the catalog.
 */
export type ChartFamilyId =
  | "comparison"
  | "ranking"
  | "trend"
  | "part_to_whole"
  | "distribution"
  | "relationship"
  | "flow"
  | "geo"
  | "summary"
  | "custom";

/**
 * Stable chart-type key. Kept as an explicit union (not derived from the
 * array) because it is also the value the backend will receive once picking a
 * type re-renders the chart — the contract should not move when copy does.
 */
export type ChartTypeId =
  | "column"
  | "stacked-column"
  | "percent-stacked-column"
  | "lollipop"
  | "diverging-bar"
  | "line"
  | "step-line"
  | "area"
  | "stacked-area"
  | "percent-stacked-area"
  | "waterfall"
  | "scatter"
  | "bubble"
  | "histogram"
  | "box-plot"
  | "heatmap"
  | "horizontal-bar"
  | "horizontal-stacked-bar"
  | "horizontal-percent-stacked-bar"
  | "funnel"
  | "pyramid"
  | "pie"
  | "donut"
  | "radial-bar"
  | "kpi"
  | "table"
  | "pivot-table"
  | "grouped-column"
  | "dot-plot"
  | "dumbbell"
  | "range-bar"
  | "bullet"
  | "butterfly"
  | "marimekko"
  | "combo"
  | "radar"
  | "word-cloud"
  | "grouped-bar"
  | "pareto"
  | "line-markers"
  | "smooth-line"
  | "streamgraph"
  | "slope"
  | "bump"
  | "candlestick"
  | "calendar-heatmap"
  | "gantt"
  | "rose"
  | "waffle"
  | "treemap"
  | "sunburst"
  | "icicle"
  | "circle-pack"
  | "violin"
  | "density"
  | "strip-plot"
  | "beeswarm"
  | "ecdf"
  | "ridgeline"
  | "scatter-trend"
  | "connected-scatter"
  | "correlation-matrix"
  | "parallel-coordinates"
  | "scatter-matrix"
  | "sankey"
  | "network"
  | "arc-diagram"
  | "tree"
  | "world-map"
  | "us-map"
  | "bubble-map"
  | "point-map"
  | "kpi-trend"
  | "gauge"
  | "progress"
  | "heatmap-table"
  | "custom-spec";

/**
 * Glyph slot key, resolved to a node by the icon registry. Every gallery id is
 * its own key (one 24px line glyph each); `barChartBig` is Figma's original
 * placeholder, kept for callers that have no type of their own.
 */
export type ChartTypeIconKey = "barChartBig" | ChartTypeId;

export interface ChartTypeCategoryConfig {
  id: ChartTypeCategoryId;
  /** Column heading copy (Figma Caption 4, e.g. "Part to whole"). */
  label: string;
}

/**
 * The v2 `binding.chart_type` values the picker can send. `combo` is left out
 * on purpose: its per-measure marks have no rules yet (guide, Known gaps).
 */
export type ChartTypeBindingValue = "bar" | "line" | "area" | "scatter" | "pie";

export interface ChartTypeConfig {
  id: ChartTypeId;
  /** Row title (Figma Body 3). Also the Configure trigger's value line. */
  label: string;
  /** Row subtitle (Figma Caption 4). Also the Configure trigger's description line. */
  description: string;
  /**
   * The v1 picker's column. Only the 27 original rows have one; the rest are
   * reached through the gallery, by `family`.
   */
  category?: ChartTypeCategoryId;
  /** The gallery's family tab (the catalog's `family` for this gallery id). */
  family: ChartFamilyId;
  icon: ChartTypeIconKey;
  /**
   * The `binding.chart_type` this row sends. Only rows that carry one are
   * offered: the rest have no v2 chart type to become (Column is `bar`).
   */
  binding?: ChartTypeBindingValue;
}

/**
 * Registry order is the grid's reading order: three columns, two rows —
 * Comparison / Trend / Distribution above Ranking / Part to whole / Summary.
 */
export const chartTypeCategories = [
  { id: "comparison", label: "Comparison" },
  { id: "trend", label: "Trend" },
  { id: "distribution", label: "Distribution" },
  { id: "ranking", label: "Ranking" },
  { id: "part-to-whole", label: "Part to whole" },
  { id: "summary", label: "Summary" },
] as const satisfies readonly ChartTypeCategoryConfig[];

export interface ChartFamilyConfig {
  id: ChartFamilyId;
  /** Tab label. */
  label: string;
  /** One line under the tab when it is open. */
  description: string;
}

/** Registry order is the gallery's tab order (the catalog's `families` order). */
export const chartFamilies = [
  { id: "comparison", label: "Comparison", description: "Compare values across categories" },
  { id: "ranking", label: "Ranking", description: "Order categories and see how they step up or down" },
  { id: "trend", label: "Trend", description: "See how values change over time" },
  { id: "part_to_whole", label: "Part to whole", description: "See how a total divides into parts" },
  { id: "distribution", label: "Distribution", description: "See how values spread out" },
  { id: "relationship", label: "Relationship", description: "See how measures relate to each other" },
  { id: "flow", label: "Flow", description: "See how items connect and move between groups" },
  { id: "geo", label: "Maps", description: "Place values on a map" },
  { id: "summary", label: "Summary", description: "Show headline numbers and tables" },
  { id: "custom", label: "Custom", description: "Write the chart yourself" },
] as const satisfies readonly ChartFamilyConfig[];

/**
 * Within a category, array order is the Figma top-to-bottom order. Copy is
 * Figma's verbatim except two typos fixed ("mwasures", "outliners") and
 * trailing spaces trimmed.
 */
export const chartTypes = [
  { id: "column", label: "Column", description: "Compare a measure across categories", category: "comparison", family: "comparison", icon: "column", binding: "bar" },
  { id: "stacked-column", label: "Stacked column", description: "Parts of a total per category", category: "comparison", family: "comparison", icon: "stacked-column" },
  { id: "percent-stacked-column", label: "100% stacked column", description: "Share of total per category", category: "comparison", family: "comparison", icon: "percent-stacked-column" },
  { id: "lollipop", label: "Lollipop", description: "Light weight ranking with dots", category: "comparison", family: "comparison", icon: "lollipop" },
  { id: "diverging-bar", label: "Diverging bar", description: "Positive vs negative values around zero", category: "comparison", family: "comparison", icon: "diverging-bar" },

  { id: "line", label: "Line", description: "Change over time", category: "trend", family: "trend", icon: "line", binding: "line" },
  { id: "step-line", label: "Step line", description: "Discrete changes between periods", category: "trend", family: "trend", icon: "step-line" },
  { id: "area", label: "Area", description: "Volume over time", category: "trend", family: "trend", icon: "area", binding: "area" },
  { id: "stacked-area", label: "Stacked area", description: "Volume split by category over time", category: "trend", family: "trend", icon: "stacked-area" },
  { id: "percent-stacked-area", label: "100% stacked area", description: "Share of total over time", category: "trend", family: "trend", icon: "percent-stacked-area" },
  { id: "waterfall", label: "Waterfall", description: "How each step builds to the total", category: "trend", family: "ranking", icon: "waterfall" },

  { id: "scatter", label: "Scatter", description: "Relationship between two measures", category: "distribution", family: "relationship", icon: "scatter", binding: "scatter" },
  { id: "bubble", label: "Bubble", description: "Two measures plus a third as size", category: "distribution", family: "relationship", icon: "bubble" },
  { id: "histogram", label: "Histogram", description: "How one measure is distributed", category: "distribution", family: "distribution", icon: "histogram" },
  { id: "box-plot", label: "Box plot", description: "Spread and outliers per category", category: "distribution", family: "distribution", icon: "box-plot" },
  { id: "heatmap", label: "Heatmap", description: "Measure intensity across two categories", category: "distribution", family: "relationship", icon: "heatmap" },

  { id: "horizontal-bar", label: "Horizontal bar", description: "Ranking with long category names", category: "ranking", family: "comparison", icon: "horizontal-bar" },
  { id: "horizontal-stacked-bar", label: "Horizontal stacked", description: "Ranked parts of a total", category: "ranking", family: "comparison", icon: "horizontal-stacked-bar" },
  { id: "horizontal-percent-stacked-bar", label: "Horizontal 100%", description: "Ranked share of total", category: "ranking", family: "comparison", icon: "horizontal-percent-stacked-bar" },
  { id: "funnel", label: "Funnel", description: "Stage by stage drop off", category: "ranking", family: "ranking", icon: "funnel" },
  { id: "pyramid", label: "Pyramid", description: "Funnel drawn smallest last", category: "ranking", family: "ranking", icon: "pyramid" },

  { id: "pie", label: "Pie", description: "Share of total across a few categories", category: "part-to-whole", family: "part_to_whole", icon: "pie", binding: "pie" },
  { id: "donut", label: "Donut", description: "Share of total with a centre label", category: "part-to-whole", family: "part_to_whole", icon: "donut" },
  { id: "radial-bar", label: "Radial bar", description: "Categories as radiating arcs", category: "part-to-whole", family: "part_to_whole", icon: "radial-bar" },

  { id: "kpi", label: "KPI", description: "Single number with periodic trend", category: "summary", family: "summary", icon: "kpi" },
  { id: "table", label: "Table", description: "Raw rows and aggregates", category: "summary", family: "summary", icon: "table" },
  { id: "pivot-table", label: "Pivot table", description: "Cross tab of dimensions and measures", category: "summary", family: "summary", icon: "pivot-table" },

  // Gallery-only types: no v1 `category`, no `binding`. Gallery order is the catalog's, not this array's.
  { id: "grouped-column", label: "Grouped column", description: "Side by side columns per category", family: "comparison", icon: "grouped-column" },
  { id: "dot-plot", label: "Dot plot", description: "A dot per category on a shared scale", family: "comparison", icon: "dot-plot" },
  { id: "dumbbell", label: "Dumbbell", description: "Two values per category, joined by a line", family: "comparison", icon: "dumbbell" },
  { id: "range-bar", label: "Range bar", description: "Low to high span per category", family: "comparison", icon: "range-bar" },
  { id: "bullet", label: "Bullet", description: "A measure against its target", family: "comparison", icon: "bullet" },
  { id: "butterfly", label: "Butterfly", description: "Two measures mirrored around a centre line", family: "comparison", icon: "butterfly" },
  { id: "marimekko", label: "Marimekko", description: "Column width and segment height both carry a measure", family: "comparison", icon: "marimekko" },
  { id: "combo", label: "Combo", description: "Bars and lines on one chart", family: "comparison", icon: "combo" },
  { id: "radar", label: "Radar", description: "Several measures per category on radial axes", family: "comparison", icon: "radar" },
  { id: "word-cloud", label: "Word cloud", description: "Words sized by a measure", family: "comparison", icon: "word-cloud" },
  { id: "grouped-bar", label: "Grouped bar", description: "Side by side horizontal bars per category", family: "comparison", icon: "grouped-bar" },
  { id: "pareto", label: "Pareto", description: "Ranked bars with a cumulative share line", family: "ranking", icon: "pareto" },
  { id: "line-markers", label: "Line with markers", description: "Change over time with a dot at each period", family: "trend", icon: "line-markers" },
  { id: "smooth-line", label: "Smooth line", description: "Change over time with curved segments", family: "trend", icon: "smooth-line" },
  { id: "streamgraph", label: "Streamgraph", description: "Volume split by category, centred around a baseline", family: "trend", icon: "streamgraph" },
  { id: "slope", label: "Slope", description: "Change between two periods per category", family: "trend", icon: "slope" },
  { id: "bump", label: "Bump", description: "How the rank of each category changes over time", family: "trend", icon: "bump" },
  { id: "candlestick", label: "Candlestick", description: "Open, high, low and close per period", family: "trend", icon: "candlestick" },
  { id: "calendar-heatmap", label: "Calendar heatmap", description: "A measure for each day of the year", family: "trend", icon: "calendar-heatmap" },
  { id: "gantt", label: "Gantt", description: "Tasks drawn from a start to an end date", family: "trend", icon: "gantt" },
  { id: "rose", label: "Rose", description: "Categories as wedges whose length shows the value", family: "part_to_whole", icon: "rose" },
  { id: "waffle", label: "Waffle", description: "Share of total as a grid of squares", family: "part_to_whole", icon: "waffle" },
  { id: "treemap", label: "Treemap", description: "Nested rectangles sized by a measure", family: "part_to_whole", icon: "treemap" },
  { id: "sunburst", label: "Sunburst", description: "A hierarchy as concentric rings", family: "part_to_whole", icon: "sunburst" },
  { id: "icicle", label: "Icicle", description: "A hierarchy as stacked horizontal bands", family: "part_to_whole", icon: "icicle" },
  { id: "circle-pack", label: "Circle pack", description: "A hierarchy as nested circles", family: "part_to_whole", icon: "circle-pack" },
  { id: "violin", label: "Violin", description: "Shape of the distribution per category", family: "distribution", icon: "violin" },
  { id: "density", label: "Density", description: "Smoothed distribution of one measure", family: "distribution", icon: "density" },
  { id: "strip-plot", label: "Strip plot", description: "Every value as a dot along one axis", family: "distribution", icon: "strip-plot" },
  { id: "beeswarm", label: "Beeswarm", description: "Every value as a dot, spread so none overlap", family: "distribution", icon: "beeswarm" },
  { id: "ecdf", label: "ECDF", description: "Share of values at or below each value", family: "distribution", icon: "ecdf" },
  { id: "ridgeline", label: "Ridgeline", description: "Distributions stacked one per category", family: "distribution", icon: "ridgeline" },
  { id: "scatter-trend", label: "Scatter with trend", description: "Two measures with a fitted trend line", family: "relationship", icon: "scatter-trend" },
  { id: "connected-scatter", label: "Connected scatter", description: "Points joined in time order", family: "relationship", icon: "connected-scatter" },
  { id: "correlation-matrix", label: "Correlation matrix", description: "How strongly each pair of measures moves together", family: "relationship", icon: "correlation-matrix" },
  { id: "parallel-coordinates", label: "Parallel coordinates", description: "One line per row across several measures", family: "relationship", icon: "parallel-coordinates" },
  { id: "scatter-matrix", label: "Scatter matrix", description: "Every pair of measures as a small scatter", family: "relationship", icon: "scatter-matrix" },
  { id: "sankey", label: "Sankey", description: "Flow between two groups, sized by a measure", family: "flow", icon: "sankey" },
  { id: "network", label: "Network", description: "Connections between items", family: "flow", icon: "network" },
  { id: "arc-diagram", label: "Arc diagram", description: "Connections drawn as arcs along a line", family: "flow", icon: "arc-diagram" },
  { id: "tree", label: "Tree", description: "Parent and child relationships", family: "flow", icon: "tree" },
  { id: "world-map", label: "World map", description: "A measure shaded by country", family: "geo", icon: "world-map" },
  { id: "us-map", label: "US map", description: "A measure shaded by state", family: "geo", icon: "us-map" },
  { id: "bubble-map", label: "Bubble map", description: "Circles sized by a measure at each place", family: "geo", icon: "bubble-map" },
  { id: "point-map", label: "Point map", description: "Points placed by latitude and longitude", family: "geo", icon: "point-map" },
  { id: "kpi-trend", label: "KPI with trend", description: "Single number with its line over time", family: "summary", icon: "kpi-trend" },
  { id: "gauge", label: "Gauge", description: "A measure on a dial against a maximum", family: "summary", icon: "gauge" },
  { id: "progress", label: "Progress", description: "A measure filled toward its goal", family: "summary", icon: "progress" },
  { id: "heatmap-table", label: "Heatmap table", description: "Cross tab with cells shaded by value", family: "summary", icon: "heatmap-table" },
  { id: "custom-spec", label: "Custom spec", description: "Write your own Vega or Vega-Lite spec", family: "custom", icon: "custom-spec" },
] as const satisfies readonly ChartTypeConfig[];
